import { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Copy,
  Check,
  DownloadSimple,
  CircleNotch,
  Feather,
  Package,
  Storefront,
  Sun,
  InstagramLogo,
  TiktokLogo,
  PlayCircle,
  SquaresFour,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import {
  socialKitMeta,
  brandTone,
  pillars,
  posts,
  stories,
  reels,
  calendar,
  hashtagBank,
  design,
} from "@/data/socialKit";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const iconMap = { Feather, Package, Storefront, Sun };

/* ---------- Small primitives ---------- */

const CopyBtn = ({ text, label = "Copy", copiedLabel = "Copied" }) => {
  const [ok, setOk] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setOk(true);
      setTimeout(() => setOk(false), 1500);
    } catch (_) {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid="copy-btn"
      className="inline-flex items-center gap-1.5 text-[11px] tracking-luxe uppercase text-[#1A1A1A]/60 hover:text-[#8B7355] transition-colors"
    >
      {ok ? <Check size={13} weight="bold" /> : <Copy size={13} />}
      <span>{ok ? copiedLabel : label}</span>
    </button>
  );
};

const SectionTitle = ({ kicker, title }) => (
  <div className="mb-10">
    <p className="text-xs tracking-luxe uppercase text-[#8B7355] mb-3">{kicker}</p>
    <h2 className="font-display text-3xl md:text-4xl leading-tight">{title}</h2>
  </div>
);

const Badge = ({ children, color = "#8B7355" }) => (
  <span
    className="inline-flex items-center gap-1.5 text-[10px] tracking-luxe uppercase px-2.5 py-1 border"
    style={{ borderColor: `${color}55`, color }}
  >
    {children}
  </span>
);

/* ---------- Mockups ---------- */

const IGPostMockup = ({ post, lang }) => {
  const caption = post.caption[lang];
  const truncated = caption.length > 110 ? caption.slice(0, 110) + "…" : caption;
  return (
    <div className="w-full max-w-[320px] bg-white border border-black/10 shadow-sm">
      {/* header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-black/5">
        <div className="w-7 h-7 rounded-full bg-[#F3EFEA] flex items-center justify-center">
          <span className="font-display text-[11px]">HZ</span>
        </div>
        <span className="text-xs font-medium">homezen.official</span>
      </div>
      {/* image area */}
      <div className="relative aspect-[4/5] bg-gradient-to-br from-[#F3EFEA] via-[#EAE4DA] to-[#D9CDBC] overflow-hidden">
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
          <span className="font-display text-[#1A1A1A]/40 text-xs tracking-luxe uppercase mb-3">
            {post.format}
          </span>
          <span className="font-display text-2xl leading-tight text-[#1A1A1A]/80">
            {caption.split("\n")[0]}
          </span>
        </div>
        {post.format === "carousel" && (
          <div className="absolute top-3 right-3 bg-black/70 text-white text-[10px] px-1.5 py-0.5">
            1/5
          </div>
        )}
      </div>
      {/* actions */}
      <div className="px-3 pt-2.5 pb-3">
        <div className="flex items-center gap-3 text-[#1A1A1A]/70 text-xs mb-2">
          <span>♥</span>
          <span>💬</span>
          <span>↗</span>
        </div>
        <p className="text-[11px] text-[#1A1A1A]/80 leading-relaxed whitespace-pre-line line-clamp-3">
          <span className="font-semibold">homezen.official</span> {truncated}
        </p>
      </div>
    </div>
  );
};

const StoryMockup = ({ story, lang }) => (
  <div className="w-full max-w-[200px]">
    <div className="relative aspect-[9/16] bg-gradient-to-b from-[#EDE6DA] via-[#F3EFEA] to-[#D9CDBC] border border-black/10 overflow-hidden shadow-sm">
      {/* top bar */}
      <div className="absolute top-0 inset-x-0 h-1 bg-white/40 mx-2 mt-2 rounded-full">
        <div className="h-full w-1/3 bg-white/90 rounded-full" />
      </div>
      {/* profile */}
      <div className="absolute top-4 left-4 flex items-center gap-1.5">
        <div className="w-6 h-6 rounded-full bg-white/70 flex items-center justify-center">
          <span className="font-display text-[9px]">HZ</span>
        </div>
        <span className="text-[9px] text-white/90 drop-shadow">homezen.official</span>
      </div>
      {/* text */}
      <div className="absolute inset-0 flex items-center justify-center p-4 text-center">
        <p className="font-display text-[15px] leading-snug text-[#1A1A1A]/85 whitespace-pre-line">
          {story.copy[lang]}
        </p>
      </div>
      {/* sticker tag */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
        <span className="text-[8px] tracking-luxe uppercase bg-white/70 px-1.5 py-0.5">
          {story.sticker}
        </span>
      </div>
    </div>
  </div>
);

const ReelMockup = ({ reel, lang }) => (
  <div className="w-full max-w-[200px]">
    <div className="relative aspect-[9/16] bg-gradient-to-br from-[#1A1A1A] via-[#3A342C] to-[#8B7355] border border-black/10 overflow-hidden shadow-sm">
      <PlayCircle
        size={44}
        weight="thin"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/70"
      />
      <div className="absolute top-3 left-3 flex items-center gap-1.5 text-white/80">
        <TiktokLogo size={13} weight="fill" />
        <span className="text-[9px]">{reel.duration}</span>
      </div>
      <div className="absolute bottom-3 left-3 right-3">
        <p className="font-display text-[13px] leading-snug text-white/90 line-clamp-3">
          {reel.hook[lang]}
        </p>
      </div>
    </div>
  </div>
);

/* ---------- Cards ---------- */

const PostCard = ({ post, lang, l }) => {
  const pillar = pillars.find((p) => p.id === post.pillar);
  const fullCopy = `${post.caption[lang]}\n\n${post.cta[lang]}\n\n${post.hashtags.join(" ")}`;
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`post-card-${post.id}`}
      className="grid md:grid-cols-[280px_1fr] gap-8 bg-[#FAFAF7] border border-black/5 p-6 md:p-8"
    >
      <div className="flex md:block justify-center">
        <IGPostMockup post={post} lang={lang} />
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Badge color={pillar.color}>{pillar.name[lang]}</Badge>
          <Badge color="#A39B8E">
            <InstagramLogo size={10} weight="light" /> {l.formats[post.format]}
          </Badge>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] tracking-luxe uppercase text-[#1A1A1A]/50">{l.labels.visual}</p>
            </div>
            <p className="text-sm text-[#1A1A1A]/70 italic">{post.visual[lang]}</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] tracking-luxe uppercase text-[#1A1A1A]/50">{l.labels.caption}</p>
              <CopyBtn text={fullCopy} label={l.labels.copy} copiedLabel={l.labels.copied} />
            </div>
            <p className="text-sm text-[#1A1A1A]/85 leading-relaxed whitespace-pre-line">
              {post.caption[lang]}
            </p>
          </div>

          <div>
            <p className="text-[10px] tracking-luxe uppercase text-[#1A1A1A]/50 mb-1.5">{l.labels.cta}</p>
            <p className="text-sm text-[#8B7355]">{post.cta[lang]}</p>
          </div>

          <div>
            <p className="text-[10px] tracking-luxe uppercase text-[#1A1A1A]/50 mb-1.5">{l.labels.hashtags}</p>
            <p className="text-sm text-[#1A1A1A]/60">{post.hashtags.join(" ")}</p>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

const StoryCard = ({ story, lang, l }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    data-testid={`story-card-${story.id}`}
    className="flex flex-col items-center gap-3"
  >
    <StoryMockup story={story} lang={lang} />
    <div className="text-center max-w-[220px]">
      <p className="text-[10px] tracking-luxe uppercase text-[#8B7355] mb-1">{story.goal[lang]}</p>
      <div className="mt-1.5">
        <CopyBtn text={story.copy[lang]} label={l.labels.copy} copiedLabel={l.labels.copied} />
      </div>
    </div>
  </motion.div>
);

const ReelCard = ({ reel, lang, l }) => {
  const scriptFull = reel.script
    .map((s) => `${s.t} — ${s.cue[lang]}`)
    .join("\n");
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`reel-card-${reel.id}`}
      className="grid md:grid-cols-[220px_1fr] gap-8 bg-[#1A1A1A] text-[#FAFAF7] p-6 md:p-8"
    >
      <div className="flex md:block justify-center">
        <ReelMockup reel={reel} lang={lang} />
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-2 mb-4 text-white/70">
          <span className="text-[10px] tracking-luxe uppercase border border-white/20 px-2.5 py-1">
            <PlayCircle size={11} className="inline mr-1" />
            {reel.duration}
          </span>
          <span className="text-[10px] tracking-luxe uppercase border border-white/20 px-2.5 py-1">
            {reel.format}
          </span>
        </div>

        <div className="space-y-5">
          <div>
            <p className="text-[10px] tracking-luxe uppercase text-white/40 mb-1.5">{l.labels.hook}</p>
            <p className="font-display text-xl leading-snug">{reel.hook[lang]}</p>
          </div>

          <div>
            <p className="text-[10px] tracking-luxe uppercase text-white/40 mb-1.5">{l.labels.music}</p>
            <p className="text-sm text-white/70 italic">{reel.music[lang]}</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] tracking-luxe uppercase text-white/40">{l.labels.script}</p>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(scriptFull)}
                className="text-[10px] tracking-luxe uppercase text-white/60 hover:text-white inline-flex items-center gap-1"
              >
                <Copy size={11} /> {l.labels.copy}
              </button>
            </div>
            <ul className="space-y-2">
              {reel.script.map((s) => (
                <li key={s.t} className="grid grid-cols-[70px_1fr] gap-3 text-sm">
                  <span className="text-[#8B7355] font-mono text-xs pt-0.5">{s.t}</span>
                  <span className="text-white/80">{s.cue[lang]}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] tracking-luxe uppercase text-white/40">{l.labels.caption}</p>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(reel.caption[lang])}
                className="text-[10px] tracking-luxe uppercase text-white/60 hover:text-white inline-flex items-center gap-1"
              >
                <Copy size={11} /> {l.labels.copy}
              </button>
            </div>
            <p className="text-sm text-white/80 whitespace-pre-line leading-relaxed">
              {reel.caption[lang]}
            </p>
          </div>

          <div>
            <p className="text-[10px] tracking-luxe uppercase text-white/40 mb-1.5">{l.labels.hashtags}</p>
            <p className="text-sm text-white/60">{reel.hashtags.join(" ")}</p>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

/* ---------- Sections ---------- */

const OverviewSection = ({ l, lang }) => (
  <section id="overview" className="mb-24">
    <SectionTitle kicker={l.sections.overview} title={l.title} />
    <p className="text-lg text-[#1A1A1A]/70 max-w-2xl leading-relaxed">{l.subtitle}</p>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
      {[
        { icon: SquaresFour, n: "12", label: "Post" },
        { icon: SquaresFour, n: "15", label: "Story" },
        { icon: PlayCircle, n: "3", label: "Reel · TikTok" },
        { icon: SquaresFour, n: "30", label: lang === "zh" ? "天日历" : lang === "en" ? "day calendar" : "giorni calendario" },
      ].map(({ icon: Icon, n, label }, i) => (
        <div key={i} className="bg-[#F3EFEA] p-6 flex flex-col items-start gap-2">
          <Icon size={18} className="text-[#8B7355]" weight="light" />
          <span className="font-display text-4xl">{n}</span>
          <span className="text-[11px] tracking-luxe uppercase text-[#1A1A1A]/60">{label}</span>
        </div>
      ))}
    </div>
  </section>
);

const ToneSection = ({ l, lang }) => (
  <section id="tone" className="mb-24">
    <SectionTitle kicker={l.sections.tone} title={brandTone[lang].voice[0]} />
    <div className="grid md:grid-cols-2 gap-8">
      <ul className="space-y-3">
        {brandTone[lang].voice.map((v, i) => (
          <li key={i} className="flex gap-3 text-[#1A1A1A]/80">
            <span className="text-[#8B7355] font-display">·</span>
            <span>{v}</span>
          </li>
        ))}
      </ul>
      <div className="bg-[#F3EFEA] p-6">
        <p className="text-[10px] tracking-luxe uppercase text-[#8B7355] mb-3">Keywords</p>
        <div className="flex flex-wrap gap-2">
          {brandTone[lang].keywords.map((k) => (
            <span key={k} className="text-xs bg-white/70 px-2.5 py-1 border border-black/5">
              {k}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const PillarsSection = ({ l, lang }) => (
  <section id="pillars" className="mb-24">
    <SectionTitle kicker={l.sections.pillars} title={lang === "zh" ? "四大内容支柱。" : lang === "en" ? "Four content pillars." : "Quattro pilastri di contenuto."} />
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
      {pillars.map((p) => {
        const Icon = iconMap[p.icon] || Feather;
        return (
          <div key={p.id} className="p-6 border border-black/5 bg-white">
            <div
              className="w-10 h-10 flex items-center justify-center mb-4"
              style={{ background: `${p.color}18`, color: p.color }}
            >
              <Icon size={20} weight="light" />
            </div>
            <p className="font-display text-xl mb-2">{p.name[lang]}</p>
            <p className="text-sm text-[#1A1A1A]/70 leading-relaxed mb-4">{p.desc[lang]}</p>
            <div className="flex items-baseline gap-1">
              <span className="font-display text-2xl" style={{ color: p.color }}>
                {p.weight}%
              </span>
              <span className="text-[10px] tracking-luxe uppercase text-[#1A1A1A]/40">
                {lang === "zh" ? "占比" : lang === "en" ? "share" : "quota"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

const CalendarSection = ({ l, lang }) => {
  const grid = useMemo(() => {
    const map = new Map();
    calendar.forEach((c) => {
      const arr = map.get(c.day) || [];
      arr.push(c);
      map.set(c.day, arr);
    });
    return map;
  }, []);
  const typeColor = { post: "#8B7355", story: "#A39B8E", reel: "#1A1A1A" };
  const typeLabel = {
    post: { it: "P", en: "P", zh: "帖" },
    story: { it: "S", en: "S", zh: "S" },
    reel: { it: "R", en: "R", zh: "R" },
  };
  return (
    <section id="calendar" className="mb-24">
      <SectionTitle
        kicker={l.sections.calendar}
        title={lang === "zh" ? "30 天内容日历。" : lang === "en" ? "30-day editorial calendar." : "Calendario editoriale a 30 giorni."}
      />
      <div className="grid grid-cols-6 md:grid-cols-10 gap-2">
        {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => {
          const items = grid.get(day) || [];
          return (
            <div key={day} className="aspect-square bg-[#F3EFEA] p-2 flex flex-col justify-between">
              <span className="text-[10px] text-[#1A1A1A]/50">{day}</span>
              <div className="flex flex-wrap gap-1">
                {items.map((it, i) => (
                  <span
                    key={i}
                    title={`${it.type} · ${it.ref}`}
                    className="text-[9px] font-medium px-1 py-0.5 text-white"
                    style={{ background: typeColor[it.type] }}
                  >
                    {typeLabel[it.type][lang]}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-4 mt-6 text-xs text-[#1A1A1A]/60">
        {["post", "story", "reel"].map((k) => (
          <span key={k} className="inline-flex items-center gap-2">
            <span className="w-3 h-3" style={{ background: typeColor[k] }} />
            {k === "post" ? (lang === "zh" ? "帖子" : lang === "en" ? "Post" : "Post") : k === "story" ? "Story" : "Reel"}
          </span>
        ))}
      </div>
    </section>
  );
};

const HashtagsSection = ({ l, lang }) => (
  <section id="hashtags" className="mb-24">
    <SectionTitle kicker={l.sections.hashtags} title={lang === "zh" ? "话题标签库。" : lang === "en" ? "Hashtag bank." : "Banca hashtag."} />
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {Object.entries(hashtagBank).map(([group, tags]) => (
        <div key={group} className="p-6 bg-[#FAFAF7] border border-black/5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] tracking-luxe uppercase text-[#8B7355]">{group}</p>
            <CopyBtn text={tags.join(" ")} label={l.labels.copy} copiedLabel={l.labels.copied} />
          </div>
          <p className="text-sm text-[#1A1A1A]/70 leading-relaxed">{tags.join(" ")}</p>
        </div>
      ))}
    </div>
  </section>
);

const DesignSection = ({ l, lang }) => (
  <section id="design" className="mb-24">
    <SectionTitle kicker={l.sections.design} title={lang === "zh" ? "视觉规范。" : lang === "en" ? "Design guidelines." : "Linee grafiche."} />

    <div className="grid md:grid-cols-2 gap-8">
      <div>
        <p className="text-[10px] tracking-luxe uppercase text-[#1A1A1A]/50 mb-3">{l.labels.palette}</p>
        <div className="grid grid-cols-5 gap-2">
          {design.palette.map((c) => (
            <div key={c.hex} className="text-center">
              <div className="aspect-square border border-black/5" style={{ background: c.hex }} />
              <p className="text-[10px] mt-1.5 text-[#1A1A1A]/60">{c.name[lang]}</p>
              <p className="text-[9px] font-mono text-[#1A1A1A]/40">{c.hex}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[10px] tracking-luxe uppercase text-[#1A1A1A]/50 mb-3">{l.labels.fonts}</p>
        <div className="space-y-3">
          {design.fonts.map((f) => (
            <div key={f.name} className="p-4 bg-[#FAFAF7] border border-black/5">
              <p className="font-display text-xl" style={{ fontFamily: f.name }}>
                {f.name}
              </p>
              <p className="text-[11px] text-[#1A1A1A]/60 mt-1">{f.use[lang]}</p>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-8 mt-10">
      <div className="bg-[#F3EFEA] p-6">
        <p className="text-[10px] tracking-luxe uppercase text-[#8B7355] mb-3">{l.labels.dos}</p>
        <ul className="space-y-2">
          {design.dos[lang].map((d, i) => (
            <li key={i} className="text-sm text-[#1A1A1A]/80 flex gap-2">
              <Check size={14} weight="bold" className="text-[#8B7355] mt-0.5 shrink-0" />
              {d}
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-[#1A1A1A] text-white/85 p-6">
        <p className="text-[10px] tracking-luxe uppercase text-white/50 mb-3">{l.labels.donts}</p>
        <ul className="space-y-2">
          {design.donts[lang].map((d, i) => (
            <li key={i} className="text-sm flex gap-2">
              <span className="text-[#8B7355] mt-0.5">×</span>
              {d}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

/* ---------- Page ---------- */

const NAV_SECTIONS = ["overview", "tone", "pillars", "posts", "stories", "reels", "calendar", "hashtags", "design"];

export default function SocialKitPage() {
  const { lang } = useLang();
  const l = socialKitMeta[lang];
  const printRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!printRef.current || downloading) return;
    setDownloading(true);
    try {
      const { default: html2pdf } = await import("html2pdf.js");
      const filename = `HomeZen-SocialKit-${lang.toUpperCase()}.pdf`;
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename,
          image: { type: "jpeg", quality: 0.92 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: "#FAFAF7",
            ignoreElements: (el) => el?.dataset?.excludePdf === "true",
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"], avoid: ["article", "section", ".pdf-avoid-break"] },
        })
        .from(printRef.current)
        .save();
    } catch (e) {
      // Graceful fallback to native print if generation fails
      window.print();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="App bg-[#FAFAF7] text-[#1A1A1A] min-h-screen">
      <div data-exclude-pdf="true">
        <Navbar />
      </div>

      <main className="pt-32 pb-20">
        <div ref={printRef} className="max-w-[1400px] mx-auto px-6 md:px-12">
          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-16"
          >
            <Link
              to="/"
              data-testid="back-home"
              data-exclude-pdf="true"
              className="inline-flex items-center gap-1.5 text-xs tracking-luxe uppercase text-[#1A1A1A]/60 hover:text-[#8B7355] transition-colors mb-6"
            >
              <ArrowLeft size={12} /> {l.backHome}
            </Link>
            <p className="text-xs tracking-luxe uppercase text-[#8B7355] mb-3">{l.kicker}</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] mb-6">{l.title}</h1>
            <p className="text-lg text-[#1A1A1A]/70 max-w-2xl mb-8">{l.subtitle}</p>

            <div className="flex flex-wrap items-center gap-3" data-exclude-pdf="true">
              <button
                type="button"
                onClick={handleDownloadPdf}
                disabled={downloading}
                data-testid="download-pdf-btn"
                className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FAFAF7] text-sm px-6 py-3.5 hover:bg-[#8B7355] transition-colors disabled:opacity-70 disabled:cursor-wait"
              >
                {downloading ? (
                  <CircleNotch size={16} weight="bold" className="animate-spin" />
                ) : (
                  <DownloadSimple size={16} weight="light" />
                )}
                {downloading
                  ? lang === "zh"
                    ? "生成中…"
                    : lang === "en"
                    ? "Generating…"
                    : "Generazione…"
                  : l.downloadPdf}
              </button>
              <span className="text-xs tracking-luxe uppercase text-[#1A1A1A]/50">{l.monthly}</span>
            </div>
          </motion.header>

          {/* Sticky in-page nav */}
          <nav
            data-exclude-pdf="true"
            className="sticky top-20 z-30 -mx-6 md:-mx-12 mb-16 border-b border-black/5 bg-[#FAFAF7]/80 backdrop-blur-2xl print:hidden"
          >
            <div className="px-6 md:px-12 py-4 flex gap-6 overflow-x-auto no-scrollbar">
              {NAV_SECTIONS.map((s) => (
                <a
                  key={s}
                  href={`#${s}`}
                  data-testid={`kit-nav-${s}`}
                  className="text-xs tracking-luxe uppercase text-[#1A1A1A]/60 hover:text-[#8B7355] transition-colors whitespace-nowrap"
                >
                  {l.sections[s]}
                </a>
              ))}
            </div>
          </nav>

          <OverviewSection l={l} lang={lang} />
          <ToneSection l={l} lang={lang} />
          <PillarsSection l={l} lang={lang} />

          {/* Posts */}
          <section id="posts" className="mb-24">
            <SectionTitle
              kicker={l.sections.posts}
              title={lang === "zh" ? "12 条 Instagram 帖子。" : lang === "en" ? "12 Instagram posts." : "12 post Instagram."}
            />
            <div className="grid gap-6">
              {posts.map((p) => (
                <PostCard key={p.id} post={p} lang={lang} l={l} />
              ))}
            </div>
          </section>

          {/* Stories */}
          <section id="stories" className="mb-24">
            <SectionTitle
              kicker={l.sections.stories}
              title={lang === "zh" ? "15 条 Story。" : lang === "en" ? "15 stories." : "15 storie."}
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
              {stories.map((s) => (
                <StoryCard key={s.id} story={s} lang={lang} l={l} />
              ))}
            </div>
          </section>

          {/* Reels */}
          <section id="reels" className="mb-24">
            <SectionTitle
              kicker={l.sections.reels}
              title={lang === "zh" ? "3 条 Reel / TikTok。" : lang === "en" ? "3 Reels / TikToks." : "3 Reel / TikTok."}
            />
            <div className="grid gap-6">
              {reels.map((r) => (
                <ReelCard key={r.id} reel={r} lang={lang} l={l} />
              ))}
            </div>
          </section>

          <CalendarSection l={l} lang={lang} />
          <HashtagsSection l={l} lang={lang} />
          <DesignSection l={l} lang={lang} />
        </div>
      </main>

      <div data-exclude-pdf="true">
        <Footer />
      </div>
    </div>
  );
}
